import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ScrollView,
  BackHandler,
  ActivityIndicator,
  Platform,
  View,
  StyleSheet,
  Pressable,
} from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack } from '@tamagui/stacks';
import { useNavigation } from '@react-navigation/native';
import { ChevronLeft } from 'lucide-react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type {
  HomeStackScreenProps,
  RootStackParamList,
} from '@/types/navigation';
import { Screen } from '@/components';
import HeroImage from './components/HeroImage';
import ActionsBar from './components/ActionsBar';
import ProfileHeader from './components/ProfileHeader';
import LocationSection from './components/LocationSection';
import AboutSection from './components/AboutSection';
import TagsSection from './components/TagsSection';
import GallerySection from './components/GallerySection';
import PhotoViewerModal from './components/PhotoViewerModal';
import { SECTION_GAP, BOTTOM_PADDING } from './constants';
import { getStyles } from './styles';
import { useMeetingAnnouncement } from '@/api/hooks/useMeetingAnnouncements';
import { usePublicProfile } from '@/api/hooks/useProfile';
import { useHobbies } from '@/api/hooks/useGendersAndHobbies';
import {
  mapAnnouncementToProfile,
  extractPhotoUrls,
  getDefaultPhotoUrl,
  getCategoryFromTypeId,
} from '@/api/mappers/announcementMappers';
import { COLORS } from '@/constants/colors';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

type Props = HomeStackScreenProps<'MeetingAnnouncement'>;

const MeetingAnnouncementScreen = React.memo<Props>(({ route }) => {
  const isMobileLayout = useIsMobileLayout();
  const { announcementId, announcement: passedAnnouncement } = route.params;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const styles = useMemo(() => getStyles(isDark), [isDark]);

  const [photoViewerVisible, setPhotoViewerVisible] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  // Fetch announcement data from API only if not passed via navigation
  const {
    data: fetchedAnnouncement,
    isLoading: isLoadingAnnouncement,
    isError: isAnnouncementError,
  } = useMeetingAnnouncement(announcementId, {
    enabled: !passedAnnouncement, // Skip fetch if announcement is already provided
  });

  // Use passed announcement if available, otherwise use fetched one
  const announcement = passedAnnouncement || fetchedAnnouncement;

  // Get userId from announcement (either passed or fetched)
  const userId = announcement?.createdById;

  // Debug: Log userId to check if it's available
  useEffect(() => {
    if (userId) {
      console.log(
        '[MeetingAnnouncementScreen] Fetching profile for userId:',
        userId,
      );
    } else {
      console.log(
        '[MeetingAnnouncementScreen] No userId available, announcement:',
        announcement?.id,
      );
    }
  }, [userId, announcement?.id]);

  // Fetch public profile of the user who created the announcement
  const {
    data: userPublicProfile,
    isLoading: isLoadingUserProfile,
    error: userProfileError,
  } = usePublicProfile(userId || '', {
    enabled: !!userId, // Only fetch if we have userId
  });

  // Debug: Log profile fetch status
  useEffect(() => {
    if (userProfileError) {
      console.error(
        '[MeetingAnnouncementScreen] Error fetching user profile:',
        userProfileError,
      );
    }
    if (userPublicProfile) {
      console.log(
        '[MeetingAnnouncementScreen] User profile loaded:',
        userPublicProfile.id,
      );
    }
  }, [userPublicProfile, userProfileError]);

  // Photos are now included in announcement.photos
  const photos = announcement?.photos || [];

  // Fetch hobbies list for mapping hobbyIds to names
  const { data: hobbies = [] } = useHobbies();

  // Map announcement to profile for display, enriched with user public profile data
  const profile = useMemo(() => {
    if (!announcement) return null;
    const mappedProfile = mapAnnouncementToProfile(announcement, photos);

    // Enrich with user public profile data if available
    if (userPublicProfile) {
      return {
        ...mappedProfile,
        name: userPublicProfile.nickName || mappedProfile.name,
        rating:
          userPublicProfile.ratingAverage?.toString() || mappedProfile.rating,
        // Use user's photos if available, otherwise use announcement photos
        photos:
          userPublicProfile.photos && userPublicProfile.photos.length > 0
            ? userPublicProfile.photos.map(p => p.originalUrl)
            : mappedProfile.photos,
        photoUrl:
          userPublicProfile.photos && userPublicProfile.photos.length > 0
            ? userPublicProfile.photos.find(p => p.isDefault)?.originalUrl ||
              userPublicProfile.photos[0].originalUrl
            : mappedProfile.photoUrl,
      };
    }

    return mappedProfile;
  }, [announcement, photos, userPublicProfile]);

  // Handle hardware back button on Android (only when photo viewer is visible)
  useEffect(() => {
    if (!photoViewerVisible) return;

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        setPhotoViewerVisible(false);
        return true; // Prevent default behavior
      },
    );

    return () => backHandler.remove();
  }, [photoViewerVisible]);

  const heroImageUri = useMemo(() => {
    // Prioritize user public profile photos
    if (userPublicProfile?.photos && userPublicProfile.photos.length > 0) {
      // Find default photo by isDefault flag or defaultPhotoId
      const defaultPhoto =
        userPublicProfile.photos.find(p => p.isDefault) ||
        (userPublicProfile.defaultPhotoId
          ? userPublicProfile.photos.find(
              p => p.id === userPublicProfile.defaultPhotoId,
            )
          : null) ||
        userPublicProfile.photos[0];

      if (defaultPhoto) {
        return defaultPhoto.originalUrl;
      }
    }

    // Fallback to announcement photos
    if (!announcement) return 'https://static.photos/people/1200x630/0';
    return getDefaultPhotoUrl(announcement, photos);
  }, [announcement, photos, userPublicProfile]);

  // Gallery photos - return full UserPhotoView objects to preserve isLocked status
  const galleryPhotos = useMemo(() => {
    // Prioritize user public profile photos (excluding the default one)
    if (userPublicProfile?.photos && userPublicProfile.photos.length > 0) {
      // Find default photo to exclude it from gallery
      const defaultPhoto =
        userPublicProfile.photos.find(p => p.isDefault) ||
        (userPublicProfile.defaultPhotoId
          ? userPublicProfile.photos.find(
              p => p.id === userPublicProfile.defaultPhotoId,
            )
          : null);

      // Get all photos except the default one (return full objects)
      const galleryPhotoObjects = userPublicProfile.photos.filter(p =>
        defaultPhoto ? p.id !== defaultPhoto.id : true,
      );

      if (galleryPhotoObjects.length > 0) {
        return galleryPhotoObjects;
      }

      // If only default photo exists, show it in gallery too
      if (userPublicProfile.photos.length > 0) {
        return userPublicProfile.photos;
      }
    }

    // Fallback to announcement photos (as string URLs)
    const photoUrls = extractPhotoUrls(photos);
    if (photoUrls.length > 0) return photoUrls;

    // Fallback to placeholder photos
    return [
      'https://static.photos/people/1200x630/3',
      'https://static.photos/people/1200x630/0',
      'https://static.photos/people/1200x630/2',
    ];
  }, [photos, userPublicProfile]);

  // Gallery photo URLs for PhotoViewerModal
  const galleryPhotoUrls = useMemo(() => {
    return galleryPhotos.map(photo =>
      typeof photo === 'string' ? photo : photo.originalUrl,
    );
  }, [galleryPhotos]);

  // Generate tags from user profile hobbies
  const tags = useMemo(() => {
    if (!userPublicProfile?.hobbyIds || !hobbies.length) return [];

    // Map hobbyIds to hobby names
    const hobbyMap = new Map(hobbies.map(h => [h.id, h.name]));

    return userPublicProfile.hobbyIds
      .map(hobbyId => {
        const hobbyName = hobbyMap.get(Number(hobbyId));
        if (hobbyName) {
          return { text: hobbyName, checked: true };
        }
        return null;
      })
      .filter((tag): tag is { text: string; checked: boolean } => tag !== null);
  }, [userPublicProfile?.hobbyIds, hobbies]);

  const handlePhotoPress = useCallback((index: number) => {
    setSelectedPhotoIndex(index);
    setPhotoViewerVisible(true);
  }, []);

  const handleClosePhotoViewer = useCallback(() => {
    setPhotoViewerVisible(false);
  }, []);

  const handleUnlockPress = useCallback(() => {
    // TODO: Implement unlock gallery via API
  }, []);

  const handleDiscard = useCallback(() => {
    // TODO: Implement discard action via API
  }, []);

  const handleLike = useCallback(() => {
    if (announcementId && announcement) {
      navigation.navigate('ApplicationCreate', {
        announcementId,
        announcement, // Pass full object to avoid refetch
      });
    }
  }, [navigation, announcementId, announcement]);

  const handleFavorite = useCallback(() => {
    // TODO: Implement favorite action via API
  }, []);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // Loading state - only show if we're actually fetching (no passed announcement)
  if ((!passedAnnouncement && isLoadingAnnouncement) || isLoadingUserProfile) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <YStack flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={COLORS.PRIMARY} />
          <Text fontSize={14} color="$color" opacity={0.7} marginTop="$3">
            Ładowanie profilu...
          </Text>
        </YStack>
      </Screen>
    );
  }

  // Error state - only show error if we tried to fetch and failed (and no passed announcement)
  if (!passedAnnouncement && (isAnnouncementError || !announcement)) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <YStack flex={1} alignItems="center" justifyContent="center">
          <Text fontSize={16} color="$color" opacity={0.7}>
            Nie udało się pobrać ogłoszenia
          </Text>
        </YStack>
      </Screen>
    );
  }

  // Web/Tablet layout - two columns (only if width >= 800px)
  if (Platform.OS === 'web' && !isMobileLayout && announcement) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={webStyles.scrollContent}
        >
          {/* Back button and title */}
          <View style={webStyles.header}>
            <Pressable onPress={handleBack} style={webStyles.backButton}>
              <ChevronLeft size={24} color={isDark ? '#ffffff' : '#18181b'} />
            </Pressable>
            <Text
              fontSize={20}
              fontWeight="600"
              color={isDark ? '#ffffff' : '#18181b'}
            >
              Profil użytkownika
            </Text>
          </View>

          {/* Top row: Photo + Info side by side */}
          <View style={webStyles.topRow}>
            {/* Left column - Photo + Actions */}
            <View style={webStyles.leftColumn}>
              <View
                style={[
                  webStyles.photoContainer,
                  {
                    backgroundColor: isDark ? COLORS.ZINC_900 : COLORS.ZINC_200,
                  },
                ]}
              >
                <HeroImage imageUri={heroImageUri} />
              </View>

              {/* Actions bar under photo */}
              <View style={webStyles.actionsContainer}>
                <ActionsBar
                  onDiscard={handleDiscard}
                  onLike={handleLike}
                  onFavorite={handleFavorite}
                />
              </View>
            </View>

            {/* Right column - Profile info, Location, About */}
            <View style={webStyles.rightColumn}>
              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <ProfileHeader
                  name={profile?.name ?? 'Anonim'}
                  age={announcement!.ageFrom ?? 0}
                  verified={false}
                  tagline={
                    announcement!.additionalInfo ||
                    getCategoryFromTypeId(announcement!.meetingTypeId)
                  }
                  isDark={isDark}
                  photoUrl={profile?.photoUrl}
                />
              </View>

              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <LocationSection
                  location={announcement!.meetingAddress}
                  distance={profile?.distanceKm ?? ''}
                  isDark={isDark}
                />
              </View>

              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <AboutSection
                  about={
                    userPublicProfile?.bio || 'Brak dodatkowych informacji'
                  }
                  isDark={isDark}
                />
              </View>
            </View>
          </View>

          {/* Bottom row: Tags, Gallery - full width */}
          <View style={webStyles.bottomRow}>
            {tags.length > 0 && (
              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <TagsSection tags={tags} isDark={isDark} />
              </View>
            )}

            <View
              style={[
                webStyles.infoCard,
                { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
              ]}
            >
              <GallerySection
                photos={galleryPhotos}
                onPhotoPress={handlePhotoPress}
                onUnlockPress={handleUnlockPress}
                isDark={isDark}
              />
            </View>
          </View>
        </ScrollView>

        <PhotoViewerModal
          visible={photoViewerVisible}
          imageUri={galleryPhotoUrls[selectedPhotoIndex]}
          onClose={handleClosePhotoViewer}
        />
      </Screen>
    );
  }

  // Mobile layout - single column (or web with width < 800px)
  if (!announcement) {
    return null; // Should not happen due to earlier checks, but TypeScript needs this
  }

  return (
    <Screen edges={['top', 'bottom', 'left', 'right']}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: BOTTOM_PADDING },
        ]}
        style={styles.scrollView}
      >
        <YStack style={styles.container}>
          {/* Hero Image */}
          <YStack>
            <HeroImage imageUri={heroImageUri} />
          </YStack>

          {/* White Card with Actions and Profile Info */}
          <YStack style={styles.contentCard}>
            {/* Actions Bar at the top of the card */}
            <YStack alignItems="center" marginBottom="$4">
              <ActionsBar
                onDiscard={handleDiscard}
                onLike={handleLike}
                onFavorite={handleFavorite}
              />
            </YStack>

            <YStack gap={SECTION_GAP}>
              <ProfileHeader
                name={profile?.name ?? 'Anonim'}
                age={announcement.ageFrom ?? 0}
                verified={false}
                tagline={
                  announcement.additionalInfo ||
                  getCategoryFromTypeId(announcement.meetingTypeId)
                }
                isDark={isDark}
                photoUrl={profile?.photoUrl}
              />

              <LocationSection
                location={announcement.meetingAddress}
                distance={profile?.distanceKm ?? ''}
                isDark={isDark}
              />

              <AboutSection
                about={
                  userPublicProfile?.bio ||
                  announcement.additionalInfo ||
                  'Brak dodatkowych informacji'
                }
                isDark={isDark}
              />

              <TagsSection tags={tags} isDark={isDark} />

              <GallerySection
                photos={galleryPhotos}
                onPhotoPress={handlePhotoPress}
                onUnlockPress={handleUnlockPress}
                isDark={isDark}
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>

      <PhotoViewerModal
        visible={photoViewerVisible}
        imageUri={galleryPhotoUrls[selectedPhotoIndex]}
        onClose={handleClosePhotoViewer}
      />
    </Screen>
  );
});

const webStyles = StyleSheet.create({
  scrollContent: {
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  topRow: {
    flexDirection: 'row',
    gap: 16,
  },
  bottomRow: {
    marginTop: 60,
    gap: 12,
  },
  leftColumn: {
    width: '40%',
    minWidth: 200,
  },
  rightColumn: {
    flex: 1,
    gap: 12,
  },
  photoContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    aspectRatio: 3 / 4,
  },
  infoCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  actionsContainer: {
    marginTop: 8,
    alignItems: 'center',
  },
});

MeetingAnnouncementScreen.displayName = 'MeetingAnnouncementScreen';

export default MeetingAnnouncementScreen;
